import { v4 } from "uuid";
import { PostImage } from "../socialPost/interfaces/postImage";
import { getAspectRatio, getBase64 } from "../utils";

/** Lado largo máximo (px): encaja en límites habituales de Instagram/Facebook para fotos. */
const MAX_LONG_EDGE_PX = 2048;
/** Tope conservador para sessionStorage + APIs (varias redes ~4–8 MB; aquí 4 MB). */
const MAX_FILE_BYTES = 4 * 1024 * 1024;

function loadImageElement(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("IMAGE_LOAD"));
    };
    img.src = url;
  });
}

function canvasToJpegBlob(
  canvas: HTMLCanvasElement,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("TO_BLOB"))),
      "image/jpeg",
      quality
    );
  });
}

/**
 * Escala y comprime a JPEG para redes: reduce peso y dimensiones extremas
 * (muchas APIs fallan con archivos muy pesados o bordes > 8192 px).
 */
export async function prepareBlobForSocialPost(inputBlob: Blob): Promise<{
  blob: Blob;
  width: number;
  height: number;
}> {
  const img = await loadImageElement(inputBlob);
  const sw = img.naturalWidth || 1;
  const sh = img.naturalHeight || 1;
  const longEdge = Math.max(sw, sh);
  let scale = Math.min(1, MAX_LONG_EDGE_PX / longEdge);
  let cw = Math.max(1, Math.round(sw * scale));
  let ch = Math.max(1, Math.round(sh * scale));

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("NO_2D_CONTEXT");
  }
  canvas.width = cw;
  canvas.height = ch;
  ctx.drawImage(img, 0, 0, cw, ch);

  let quality = 0.9;
  for (let i = 0; i < 14; i++) {
    const b = await canvasToJpegBlob(canvas, quality);
    if (b.size <= MAX_FILE_BYTES) {
      return { blob: b, width: cw, height: ch };
    }
    quality -= 0.05;
    if (quality < 0.4) break;
  }

  let factor = 0.88;
  while (factor > 0.28) {
    cw = Math.max(1, Math.round(cw * factor));
    ch = Math.max(1, Math.round(ch * factor));
    canvas.width = cw;
    canvas.height = ch;
    ctx.drawImage(img, 0, 0, cw, ch);
    for (let q = 0.88; q >= 0.4; q -= 0.05) {
      const b = await canvasToJpegBlob(canvas, q);
      if (b.size <= MAX_FILE_BYTES) {
        return { blob: b, width: cw, height: ch };
      }
    }
    factor -= 0.07;
  }

  const last = await canvasToJpegBlob(canvas, 0.4);
  return { blob: last, width: cw, height: ch };
}

export async function buildPostImageFromBlobForSocial(
  blob: Blob,
  filename: string
): Promise<PostImage> {
  const { blob: prepared, width, height } = await prepareBlobForSocialPost(blob);
  const data = (await getBase64(prepared)) as string;
  return {
    id: v4(),
    lastModified: Date.now(),
    name: filename.endsWith(".jpg") ? filename : `${filename.replace(/\.[^.]+$/, "")}.jpg`,
    size: prepared.size,
    type: "image/jpeg",
    data,
    width,
    height,
    aspectRatio: getAspectRatio(width, height),
  };
}
